/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

// @mui material components
import Icon from '@mui/material/Icon';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { CircularProgress } from '@mui/material';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';
import Swal from 'sweetalert2';

function initStepper({ icon, completed }) {
  const icons_complete = {
    1: <Icon color='info'>request_page</Icon>,
    2: <Icon color='info'>qr_code_scanner</Icon>,
    3: <Icon color='info'>camera_alt</Icon>,
    4: <Icon color='info'>hourglass_top</Icon>,
  };
  const icons = {
    1: <Icon color='disabled'>request_page</Icon>,
    2: <Icon color='disabled'>qr_code_scanner</Icon>,
    3: <Icon color='disabled'>camera_alt</Icon>,
    4: <Icon color='disabled'>hourglass_top</Icon>,
  };
  return <div>{completed ? icons_complete[String(icon)] : icons[String(icon)]}</div>;
}

const stepperName = {
  edit: {
    name: 'ระบุเลขบิล',
    color: 'warning',
    icon: 'request_page',
  },
  scan: {
    name: 'สแกนสินค้า',
    color: 'info',
    icon: 'qr_code_scanner',
  },
  photo: {
    name: 'ถ่ายภาพ',
    color: 'info',
    icon: 'camera_alt',
  },
  request: {
    name: 'ขออนุมัติ',
    color: 'info',
    icon: 'hourglass_top',
  },
};

const initAttr = {
  id: '',
  name: '',
  enable: 'Y',
  created_at: '',
  updated_at: '',
};

const initFind = {
  barcode: '',
  quantity: '',
};

const initBill = {
  id: '',
  code: '',
  invoice_code: '',
  customer_id: '0',
  branch_id: '',
  branchsend_id: '0',
  car_id: '0',
  branch_receive: '',
  user_id: '',
  cashier_id: '',
  shift_id: '',
  shiftlist_id: '0',
  account_id: '0',
  quotation_id: '0',
  shipping_id: '',
  creditcard_id: '0',
  qrcode: '',
  type_id: '',
  vat_id: '0',
  copy_id: '0',
  payment_type: '',
  sale_type: '',
  date_record: '0000-00-00',
  date_due: '0000-00-00',
  date_shipping: '0000-00-00',
  time_shipping: '00:00:00',
  total: '0.00',
  vat: '0.00',
  vat_value: '0.00',
  shipping: '0.00',
  discount: '0.00',
  discount_type: 'B',
  discount_unit: 'Y',
  deposit: '0.00',
  discount_value: '0.00',
  discount_percent: '0.00',
  amount: '0.00',
  amount_vat: '0.00',
  nettotal: '0.00',
  paid: '0.00',
  balance: '0.00',
  money_receive: '0.00',
  money_change: '0.00',
  wallet_before: '',
  wallet_after: '',
  cutstock: 'N',
  remark: '',
  address: '',
  address2: '',
  print_no: '0',
  printso_no: '0',
  printinvoice_no: '0',
  approve: 'N',
  remark_approve: '',
  limit_approve: '0.00',
  status: 'edit',
  created_at: '',
  updated_at: '',
  customer_code: '',
  customer_name: '',
  emp_name: '',
  branch_name: '',
  shift_code: '',
  cashier_code: '',
  cashier_name: '',
};

function PosReturn({ reload, setBlockScan, sizeButton, buttonLoading, setButtonLoading }) {
  const mod = 'pos_return';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [attr, setAttr] = useState(initAttr);
  const [record, setRecord] = useState([]);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(false);
  const [stepLoading, setStepLoading] = useState(false);
  const [modalAdd, setModalAdd] = useState(false);
  const [notify, setNotify] = useState(null);
  const [activeStep, setActiveStep] = useState(1);
  const [imageScan, setImageScan] = useState('');
  const [imageQR, setImageQR] = useState('');
  const [image, setImage] = useState([]);
  const [billFound, setBillFound] = useState('NONE');
  const [bill, setBill] = useState(initBill);
  const [billList, setBillList] = useState([]);
  const [searchBill, setSearchBill] = useState('');
  const [totalRequest, setTotalRequest] = useState('');
  const txtBill = useRef(null);
  const txtSearch = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    setButtonLoading(loading);
  }, [loading]);

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );

    setTimeout(function () {
      setNotify(null);
    }, 5000);
  };

  const close = () => {
    setModalAdd(false);
    setBlockScan(false);

    setTimeout(function () {
      btnRef.current.blur();
    }, 100);
  };

  const view = () => {
    setLoading(true);
    setBlockScan(true);
    setActiveStep(1);

    setAttr(initAttr);
    setRecord([]);
    setImageScan('');
    setImageQR('');
    setImage([]);
    setFind(initFind);
    setBill(initBill);
    setBillList([]);
    setSearchBill('');
    setTotalRequest('');
    setBillFound('NONE');

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setAttr(result.data);
          setRecord(result.list);
          setImageScan(result.mobile);
          setImageQR(result.qrcode);
          setModalAdd(true);

          setTimeout(function () {
            txtBill.current.focus();
          }, 500);
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const saleOrder = () => {
    setStepLoading(true);
    setBillFound('SEARCH');

    FetchData('OPTIONS', `/${mod}/bill`, app.token, {
      lang: app.lang,
      search: searchBill,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setBillFound('OK');
          setBill(result.data);
          setBillList(result.list);
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          setBillFound('NOTFOUND');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setStepLoading(false);
      });
  };

  const select = () => {
    if (billFound === 'OK') {
      FetchData('OPTIONS', `/${mod}/select`, app.token, {
        lang: app.lang,
        search: searchBill,
      })
        .then((result) => {
          if (result.status && result.status === 'ok') {
            setBillFound('OK');
            setActiveStep(activeStep + 1);

            setTimeout(function () {
              txtSearch.current.focus();
            }, 500);
          } else if (!result.status) {
            showNotify(t('alert.server_timeout'), 'error');
          } else {
            setBillFound('NOTFOUND');
          }
        })
        .catch((error) => {
          showNotify(t('alert.network_timeout'), 'error');
        });
    }
  };

  const add = (data) => {
    if (data.barcode === '') return;
    setStepLoading(true);

    FetchData('PUT', `/${mod}`, app.token, {
      barcode: data.barcode,
      quantity: data.quantity,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setAttr(result.data);
          setRecord(result.list);
          setFind(initFind);
          showNotify(t(`alert.addcomplete`), 'success');
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setStepLoading(false);
      });
  };

  const del = (item) => {
    FetchData('DELETE', `/${mod}/${item.id}`, app.token, {})
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setAttr(result.data);
          setRecord(result.list);
          showNotify(t(`alert.deletecomplete`), 'success');
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      });
  };

  const scan = () => {
    FetchData('OPTIONS', `/${mod}/scan`, app.token, {})
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setAttr(result.data);
          setActiveStep(activeStep + 1);
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      });
  };

  const waiting = () => {
    FetchData('OPTIONS', `/${mod}/waiting`, app.token, {})
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setAttr(result.data);
          setRecord(result.list);
          setImage(result.image);
          setActiveStep(activeStep + 1);
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      });
  };

  const request = () => {
    FetchData('OPTIONS', `/${mod}/request`, app.token, {
      total_request: totalRequest * 1 === 0 ? attr.nettotal : totalRequest,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setAttr(result.data);
          close();
          Swal.fire({
            icon: 'success',
            title: 'ขออนุมัติเรียบร้อยแล้ว',
            text: 'รอการอนุมัติ',
          });
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      });
  };

  const cancel = () => {
    FetchData('OPTIONS', `/${mod}/cancel`, app.token, {})
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setModalAdd(false);
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      });
  };

  const refresh = () => {
    FetchData('OPTIONS', `/${mod}/refresh`, app.token, {})
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setAttr(result.data || initAttr);
          setRecord(result.list || []);
          setImage(result.image || []);
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      });
  };

  const handleNext = () => {
    if (activeStep === 1) {
      select();
    } else if (activeStep === 2) {
      scan();
    } else if (activeStep === 3) {
      waiting();
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const btnDel = (item) => {
    if (activeStep !== 2) return null;
    return (
      <Tooltip title='ลบ'>
        <MDButton variant='text' color='error' size='small' iconOnly onClick={() => del(item)}>
          <Icon>delete</Icon>
        </MDButton>
      </Tooltip>
    );
  };

  const renderStepper = () => {
    const stepLevel = (key) => {
      return (
        <Step sx={{ minWidth: 150 }}>
          <StepLabel StepIconComponent={initStepper}>
            <MDTypography variant='subtitle2'>{stepperName[key].name}</MDTypography>
          </StepLabel>
        </Step>
      );
    };

    return (
      <MDBox className='stepper' mt={3} mb={1}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {stepLevel('edit')}
          {stepLevel('scan')}
          {stepLevel('photo')}
          {stepLevel('request')}
        </Stepper>
      </MDBox>
    );
  };

  const txtTotalRequest = () => {
    return (
      <MDBox noValidate component='form' role='form'>
        <MDInput
          onChange={(e) => setTotalRequest(e.target.value)}
          onFocus={(e) => e.target.select()}
          value={totalRequest}
          variant='standard'
          type='number'
          InputProps={{
            inputProps: {
              maxLength: 20,
              autoComplete: 'off',
              className: 'text-right',
              sx: { minWidth: 80, fontWeight: 'bold', fontSize: '1rem' },
            },
          }}
          fullWidth
        />
      </MDBox>
    );
  };

  const renderList = () => {
    return (
      <MDBox>
        <TableContainer sx={{ boxShadow: 0 }}>
          <Table aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell align='center' width={20}>
                  No.
                </TableCell>
                <TableCell width={100}>Barcode</TableCell>
                <TableCell>สินค้า</TableCell>
                <TableCell align='right' width={90}>
                  ราคา
                </TableCell>
                <TableCell align='right' width={90}>
                  จำนวน
                </TableCell>
                <TableCell align='left' width={50}>
                  หน่วย
                </TableCell>
                <TableCell align='right' width={90}>
                  รวม
                </TableCell>
                <TableCell width={10}>&nbsp;</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {record.map((row, i) => (
                <TableRow hover key={row.id}>
                  <TableCell align='center'>{i + 1}</TableCell>
                  <TableCell>{row.barcode}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell align='right'>{Func.numberDisplay(row.price)}</TableCell>
                  <TableCell align='right'>{Func.numberDisplay(row.quantity)}</TableCell>
                  <TableCell align='left' className='nowrap'>
                    {row.unit_name}
                  </TableCell>
                  <TableCell align='right'>{Func.numberDisplay(row.total)}</TableCell>
                  <TableCell align='right'>{btnDel(row)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell align='right' colSpan={6}>
                  <MDTypography variant='h6'>รวม</MDTypography>
                </TableCell>
                <TableCell align='right'>
                  <MDTypography variant='h6'>{Func.numberDisplay(attr.nettotal)}</MDTypography>
                </TableCell>
                <TableCell />
              </TableRow>
              {activeStep === 4 ? (
                <TableRow>
                  <TableCell align='right' colSpan={6}>
                    <MDTypography variant='h6'>ส่วนลดที่ต้องการ</MDTypography>
                  </TableCell>
                  <TableCell align='right' colSpan={2}>
                    <MDTypography variant='h6'>{txtTotalRequest()}</MDTypography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
    );
  };

  const renderPhoto = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2} lg={2}>
            &nbsp;
          </Grid>
          <Grid item xs={12} md={3} lg={3}>
            <MDBox className='card' p={2}>
              <MDBox>
                <img src={imageQR} alt='' width='100%' />
              </MDBox>
              <MDBox align='center'>
                <MDTypography>SCAN QR CODE</MDTypography>
              </MDBox>
            </MDBox>
          </Grid>
          <Grid item xs={12} md={1} lg={1}>
            &nbsp;
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <img src={imageScan} alt='' width='100%' />
          </Grid>
          <Grid item xs={12} md={2} lg={2}>
            &nbsp;
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderImage = () => {
    return (
      <Grid container spacing={2}>
        {image.map((item) => (
          <Grid item xs={12} md={4} key={item.id}>
            <img src={item.image} alt={item.title} width='100%' />
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderApprove = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {attr.status === 'edit' ? (
              <MDBox display='flex' justifyContent='end' gap={1}>
                <MDButton variant='gradient' color='error' onClick={cancel}>
                  <Icon>cancel</Icon>&nbsp;ยกเลิก
                </MDButton>
                <MDButton variant='gradient' color='info' onClick={request}>
                  <Icon>check_circle</Icon>&nbsp;ขออนุมัติ
                </MDButton>
              </MDBox>
            ) : (
              <Alert
                variant='outlined'
                severity='warning'
                action={
                  <MDButton size='small' onClick={refresh}>
                    <Icon>refresh</Icon> Refresh
                  </MDButton>
                }
              >
                รออนุมัติจากผู้ดูแลระบบ
              </Alert>
            )}
          </Grid>
          <Grid item xs={12}>
            <MDBox className='card'>{renderList()}</MDBox>
          </Grid>
          {image.length === 0 ? null : (
            <Grid item xs={12}>
              <MDBox className='card' p={2} mb={2}>
                {renderImage()}
              </MDBox>
            </Grid>
          )}
        </Grid>
      </MDBox>
    );
  };

  const renderSearch = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12} md={9}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                add(find);
              }}
            >
              <MDInput
                name='searchform'
                type='search'
                fullWidth
                onChange={(e) => setFind({ ...find, barcode: e.target.value })}
                value={find.barcode}
                InputProps={{
                  inputRef: txtSearch,
                  startAdornment: (
                    <InputAdornment position='start'>
                      <span>Barcode :</span>
                    </InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={3}>
            <MDInput
              type='number'
              onChange={(e) => setFind({ ...find, quantity: e.target.value })}
              value={find.quantity}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>ปริมาณ :</span>
                  </InputAdornment>
                ),
                autoComplete: 'off',
              }}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderData = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            {renderList()}
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderSlip = () => {
    if (billFound === 'NOTFOUND') {
      return (
        <MDBox>
          <Grid container spacing={1}>
            <Grid item xs={12} align='center'>
              <Alert severity='error'>ไม่พบข้อมูล</Alert>
            </Grid>
          </Grid>
        </MDBox>
      );
    }

    if (billFound === 'OK') {
      return (
        <MDBox className='card' p={2} sx={{ maxWidth: 400, margin: 'auto' }}>
          <MDBox align='center' mb={2}>
            <MDTypography variant='h5'>ใบเสร็จ</MDTypography>
          </MDBox>
          <Grid container>
            <Grid item xs={12} align='center'>
              <MDTypography variant='body2'>{bill.branch_name}</MDTypography>
            </Grid>

            <Grid item xs={12}>
              {billList.map((row) => (
                <Grid container key={row.id}>
                  <Grid item xs={6}>
                    <MDBox>
                      <MDTypography variant='body2'>
                        {row.code} (x {row.quantity})
                      </MDTypography>
                    </MDBox>
                    <MDBox>
                      <MDTypography variant='body2'>{row.name}</MDTypography>
                    </MDBox>
                  </Grid>
                  <Grid item xs={6} align='right'>
                    <MDTypography variant='body2'>{row.price} ฿</MDTypography>
                  </Grid>
                </Grid>
              ))}
            </Grid>
            <Grid item xs={12}>
              &nbsp;
            </Grid>
            <Grid item xs={6}>
              <MDTypography variant='body'>Total</MDTypography>
            </Grid>
            <Grid item xs={6} align='right'>
              <MDTypography variant='body'>{Func.numberDisplay(bill.total)}</MDTypography>
            </Grid>
            <Grid item xs={12}>
              &nbsp;
            </Grid>
            <Grid item xs={12}>
              <MDTypography variant='body'>Cashier : {bill.emp_name}</MDTypography>
            </Grid>
            <Grid item xs={12}>
              <MDTypography variant='body'>{Func.dateTimeDisplay(bill.created_at)}</MDTypography>
            </Grid>
          </Grid>
        </MDBox>
      );
    }

    return null;
  };

  const renderBill = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                saleOrder();
              }}
            >
              <MDInput
                name='searchform'
                type='search'
                fullWidth
                onChange={(e) => setSearchBill(e.target.value)}
                onBlur={() => saleOrder()}
                value={searchBill}
                InputProps={{
                  inputRef: txtBill,
                  startAdornment: (
                    <InputAdornment position='start'>
                      <span>เลขที่บิล :</span>
                    </InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12}>
            {renderSlip()}
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderStep = () => {
    if (activeStep === 1) return renderBill();
    if (activeStep === 2) return renderData();
    if (activeStep === 3) return renderPhoto();
    if (activeStep === 4) return renderApprove();

    return null;
  };

  const renderAdd = () => {
    return (
      <Dialog open={modalAdd} maxWidth='lg' keepMounted aria-describedby='dialog-add' fullWidth>
        <DialogTitle>
          <i className='fad fa-hand-holding-box color-danger' /> &nbsp; เปลี่ยนสินค้า
          <IconButton
            aria-label='close'
            onClick={close}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {renderStepper()}
              </Grid>
              <Grid item xs={12}>
                <MDBox height={400}>{renderStep()}</MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Grid container>
            <Grid item xs={6}>
              <MDButton variant='text' color='dark' disabled={activeStep < 2} onClick={handleBack}>
                <Icon>chevron_left</Icon>&nbsp;ย้อนกลับ
              </MDButton>
            </Grid>
            <Grid item xs={6} display='flex' justifyContent='flex-end'>
              <MDButton
                variant='text'
                color='info'
                disabled={activeStep > 3 || billFound === 'NOTFOUND' || billFound === 'NONE'}
                onClick={handleNext}
              >
                ถัดไป &nbsp; <Icon>chevron_right</Icon>
              </MDButton>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <MDBox>
      <Tooltip title='Change product'>
        <MDButton
          variant='contained'
          color='dark'
          size='large'
          fullWidth
          sx={{ fontSize: sizeButton }}
          onClick={view}
          disabled={loading || buttonLoading}
          ref={btnRef}
        >
          {loading ? (
            <CircularProgress sx={{ fontSize: sizeButton }} color='inherit' />
          ) : (
            <i className='fad fa-hand-holding-box' />
          )}
          &nbsp; เปลี่ยนของ
        </MDButton>
      </Tooltip>
      {notify}

      {renderAdd()}
    </MDBox>
  );
}

PosReturn.propTypes = {
  reload: PropTypes.func.isRequired,
  setBlockScan: PropTypes.func.isRequired,
  sizeButton: PropTypes.number.isRequired,
  buttonLoading: PropTypes.bool.isRequired,
  setButtonLoading: PropTypes.func.isRequired,
};

export default PosReturn;
